// ============================================
// FILE 10: /frontend/src/pages/MyChamaInterestsPage.jsx
// PURPOSE: View user's submitted shares interests
// CHANGES: Updated for shares model
// ============================================

import { useState, useEffect } from 'react';
import { Users, Loader2, Clock, CheckCircle, XCircle, AlertCircle, TrendingUp } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { chamaAPI } from '../api/chama.api';
import { useNavigate } from 'react-router-dom';

export default function MyChamaInterestsPage() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchInterests();
    }
  }, [user]);

  const fetchInterests = async () => {
    try {
      const response = await chamaAPI.getUserChamaInterests(user.id);
      setInterests(response.interests);
    } catch (error) {
      console.error('Failed to fetch interests:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load your shares interests.',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      case 'DEPOSIT_PAID':
        return <TrendingUp className="w-4 h-4" />;
      case 'CONFIRMED':
        return <CheckCircle className="w-4 h-4" />;
      case 'DECLINED':
      case 'REFUNDED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'DEPOSIT_PAID':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'DECLINED':
      case 'REFUNDED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const calculateOwnership = (shares, totalShares) => {
    return ((shares / totalShares) * 100).toFixed(2);
  };

  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader className="p-4 sm:p-5 md:p-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl">
                <Users className="w-5 h-5 sm:w-6 sm:h-6" />
                My Shares Interests ({interests.length})
              </CardTitle>
            </CardHeader>

            <CardContent className="p-3 sm:p-4 md:p-6">
              {interests.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">
                    No Shares Interests Yet
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                    Start investing by buying shares in properties
                  </p>
                  <Button onClick={() => navigate('/properties')} className="h-9 sm:h-10 text-xs sm:text-sm">
                    Browse Properties
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {interests.map((interest) => (
                    <Card key={interest.id} className="overflow-hidden">
                      <div className="flex flex-col lg:flex-row">
                        {/* Image Section */}
                        <div
                          onClick={() => navigate(`/listing/${interest.listing.id}`)}
                          className="w-full lg:w-48 h-48 lg:h-auto bg-gray-200 cursor-pointer flex-shrink-0"
                        >
                          {interest.listing.imageUrl ? (
                            <img
                              src={interest.listing.imageUrl}
                              alt={interest.listing.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs sm:text-sm">
                              No Image
                            </div>
                          )}
                        </div>

                        {/* Content Section */}
                        <div className="flex-1 p-3 sm:p-4 space-y-2 sm:space-y-3">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3
                                onClick={() => navigate(`/listing/${interest.listing.id}`)}
                                className="font-semibold text-sm sm:text-base md:text-lg cursor-pointer hover:text-primary line-clamp-1"
                              >
                                {interest.listing.title}
                              </h3>
                              <p className="text-primary font-bold text-base sm:text-lg md:text-xl mt-1">
                                KES {parseFloat(interest.listing.price).toLocaleString()}
                              </p>
                            </div>
                            <Badge className={`${getStatusColor(interest.status)} flex items-center gap-1 text-[10px] sm:text-xs px-2 py-1`}>
                              {getStatusIcon(interest.status)}
                              {interest.status.replace('_', ' ')}
                            </Badge>
                          </div>

                          {/* Shares Details */}
                          <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            <div className="bg-purple-50 rounded p-2 sm:p-3 border border-purple-200">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">
                                Shares Purchased
                              </p>
                              <p className="font-semibold text-xs sm:text-sm md:text-base">
                                {interest.sharesWanted}
                              </p>
                            </div>
                            <div className="bg-purple-50 rounded p-2 sm:p-3 border border-purple-200">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">
                                Ownership
                              </p>
                              <p className="font-semibold text-xs sm:text-sm md:text-base">
                                {calculateOwnership(interest.sharesWanted, interest.listing.totalShares)}%
                              </p>
                            </div>
                            <div className="bg-purple-50 rounded p-2 sm:p-3 border border-purple-200">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">
                                Total Amount
                              </p>
                              <p className="font-semibold text-xs sm:text-sm md:text-base">
                                KES {parseFloat(interest.totalAmount).toLocaleString()}
                              </p>
                            </div>
                            <div className="bg-purple-50 rounded p-2 sm:p-3 border border-purple-200">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">
                                Submitted
                              </p>
                              <p className="font-semibold text-xs sm:text-sm md:text-base">
                                {new Date(interest.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>

                          {/* Deposit Info */}
                          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-2 sm:p-3 border border-purple-200">
                            <div className="flex justify-between items-center">
                              <span className="text-xs sm:text-sm font-semibold text-purple-900">
                                Deposit Required (10%):
                              </span>
                              <span className="font-bold text-sm sm:text-base text-purple-900">
                                KES {parseFloat(interest.depositRequired).toLocaleString()}
                              </span>
                            </div>
                            {interest.depositPaid && (
                              <div className="mt-1 flex items-center gap-1 text-[10px] sm:text-xs text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Deposit verified by admin
                              </div>
                            )}
                          </div>

                          {/* Funding Status */}
                          {interest.listing.chamaClosed ? (
                            <div className="bg-green-50 rounded p-2 sm:p-3 border border-green-200">
                              <p className="text-xs sm:text-sm font-semibold text-green-900">
                                ✅ Fully Funded!
                              </p>
                              <p className="text-[10px] sm:text-xs text-green-700">
                                Full payment due within 90 days
                              </p>
                            </div>
                          ) : (
                            <div className="bg-blue-50 rounded p-2 sm:p-3 border border-blue-200">
                              <p className="text-xs sm:text-sm font-semibold text-blue-900">
                                {interest.listing.sharesSold} / {interest.listing.totalShares} shares sold
                              </p>
                              <p className="text-[10px] sm:text-xs text-blue-700">
                                {((interest.listing.sharesSold / interest.listing.totalShares) * 100).toFixed(1)}% funded
                              </p>
                            </div>
                          )}

                          {/* Notes */}
                          {interest.notes && (
                            <div className="bg-gray-50 rounded p-2 sm:p-3 border">
                              <p className="text-[10px] sm:text-xs text-muted-foreground mb-1">
                                Your Notes:
                              </p>
                              <p className="text-xs sm:text-sm text-gray-700 line-clamp-2">
                                {interest.notes}
                              </p>
                            </div>
                          )}

                          {/* Admin Notes */}
                          {interest.adminNotes && (
                            <div className="bg-blue-50 rounded p-2 sm:p-3 border border-blue-200">
                              <p className="text-[10px] sm:text-xs text-blue-700 font-semibold mb-1">
                                Admin Response:
                              </p>
                              <p className="text-xs sm:text-sm text-blue-900">
                                {interest.adminNotes}
                              </p>
                            </div>
                          )}

                          {/* Action Button */}
                          <Button
                            onClick={() => navigate(`/listing/${interest.listing.id}`)}
                            variant="outline"
                            className="w-full h-8 sm:h-9 text-xs sm:text-sm"
                          >
                            View Property
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}